import React, { useState, useEffect, useRef } from "react";
import { Bot, X, Send } from "lucide-react";
import { api } from "../../api";

/**
 * Chatbot popup matching the template: gradient blue/purple header,
 * user bubbles with pink/orange gradient, bot bubbles pale blue.
 */
export default function TasksChatbot({ onClose }) {
  const [msgs, setMsgs] = useState([
    { role: "user", text: "Give me a summary of task counts by status.", time: new Date() },
  ]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(`tp-chat-${Date.now()}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    // Auto-send the initial user prompt on open
    (async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/tasks/ai/chat", { message: msgs[0].text, session_id: sessionId.current });
        setMsgs(m => [...m, { role: "bot", text: data.reply, time: new Date() }]);
      } catch { setMsgs(m => [...m, { role: "bot", text: "AI unavailable right now.", time: new Date() }]); }
      setBusy(false);
    })();
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" });
  }, [msgs, busy]);

  const send = async () => {
    if (!text.trim() || busy) return;
    const msg = text.trim();
    setMsgs(m => [...m, { role: "user", text: msg, time: new Date() }]);
    setText("");
    setBusy(true);
    try {
      const { data } = await api.post("/tasks/ai/chat", { message: msg, session_id: sessionId.current });
      setMsgs(m => [...m, { role: "bot", text: data.reply, time: new Date() }]);
    } catch { setMsgs(m => [...m, { role: "bot", text: "AI unavailable right now.", time: new Date() }]); }
    setBusy(false);
  };

  const time = (d) => new Date(d).toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", "");

  return (
    <div className="absolute bottom-4 right-4 w-[420px] max-w-[92%] h-[520px] max-h-[85%] rounded-xl shadow-2xl overflow-hidden bg-white z-30 border border-slate-200 flex flex-col" data-testid="tp-chatbot">
      <div className="px-4 py-3 flex items-center justify-between text-white" style={{ background: "linear-gradient(90deg, #6366f1 0%, #a855f7 60%, #d946ef 100%)" }}>
        <div className="text-lg font-semibold">Chatbot</div>
        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded" data-testid="tp-chatbot-close"><X size={16} /></button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-br from-white via-white to-pink-50/30">
        {msgs.map((m, i) => (
          m.role === "user" ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[80%]">
                <div className="text-white text-sm px-4 py-3 rounded-2xl rounded-br-md shadow"
                     style={{ background: "linear-gradient(90deg, #a855f7 0%, #ec4899 55%, #f97316 100%)" }}>
                  {m.text}
                </div>
                <div className="text-[10px] text-slate-400 text-right mt-1 pr-1">{time(m.time)}</div>
              </div>
            </div>
          ) : (
            <div key={i} className="flex items-start gap-2">
              <div className="w-9 h-9 rounded-full bg-indigo-500 text-white flex items-center justify-center shadow"><Bot size={16} /></div>
              <div className="max-w-[80%]">
                <div className="text-slate-700 text-sm px-4 py-3 rounded-2xl rounded-tl-md bg-sky-50 border border-sky-100 whitespace-pre-wrap">
                  {m.text}
                </div>
                <div className="text-[10px] text-slate-400 mt-1 pl-1">{time(m.time)}</div>
              </div>
            </div>
          )
        ))}
        {busy && <div className="text-[11px] text-slate-400 pl-11">Novo is thinking…</div>}
      </div>

      <div className="p-3 border-t border-slate-200 flex items-center gap-2 bg-white">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about your tasks…"
          className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm outline-none focus:border-indigo-400"
          data-testid="tp-chatbot-input"
        />
        <button onClick={send} disabled={busy}
                className="w-9 h-9 rounded-full text-white flex items-center justify-center shadow disabled:opacity-50"
                style={{ background: "linear-gradient(90deg, #6366f1, #a855f7)" }}
                data-testid="tp-chatbot-send">
          <Send size={14} />
        </button>
      </div>
      <div className="text-center text-[10px] text-slate-400 pb-1">Powered by Novo AI</div>
    </div>
  );
}
